import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, RotateCcw, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { EMAIL } from "@/lib/images";

const QUESTIONS = [
  {
    key: "recipient",
    title: "Who are you finding a companion for?",
    options: ["My parent", "My spouse", "Myself", "Another loved one"],
    multi: false,
  },
  {
    key: "setting",
    title: "Where do they live?",
    options: ["At home", "Assisted living residence", "Care facility"],
    multi: false,
  },
  {
    key: "interests",
    title: "What do they love? Pick a few.",
    options: [
      "Walks & fresh air",
      "Cards & board games",
      "Crafts & hobbies",
      "Music & movies",
      "Stories & reminiscing",
      "Cooking & baking",
      "Gardening & plants",
      "Outings & errands",
    ],
    multi: true,
  },
  {
    key: "frequency",
    title: "How often would visits help most?",
    options: ["A few times a week", "Once a week", "Now and then"],
    multi: false,
  },
];

const RECOMMENDATIONS = {
  "Walks & fresh air": "Gentle walks along Calgary's pathways and parks",
  "Cards & board games": "Cribbage, chess and cozy puzzle afternoons",
  "Crafts & hobbies": "Knitting, painting and hands-on projects",
  "Music & movies": "Listening sessions and beloved film nights",
  "Stories & reminiscing": "Photo-album storytelling and memory sharing",
  "Cooking & baking": "Baking days and treasured family recipes",
  "Gardening & plants": "Potting, planting and garden tending",
  "Outings & errands": "Escorted trips to shops, cafés and appointments",
};

const TOTAL_STEPS = QUESTIONS.length + 1;

export const CompanionQuiz = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ recipient: "", setting: "", interests: [], frequency: "" });
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const isContactStep = step === QUESTIONS.length;
  const question = QUESTIONS[step];

  const answered = isContactStep
    ? contact.name.trim() && contact.email.trim()
    : question.multi
      ? answers.interests.length > 0
      : Boolean(answers[question.key]);

  const pick = (value) => {
    if (question.multi) {
      setAnswers((a) => ({
        ...a,
        interests: a.interests.includes(value)
          ? a.interests.filter((i) => i !== value)
          : [...a.interests, value],
      }));
    } else {
      setAnswers((a) => ({ ...a, [question.key]: value }));
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/quiz-leads", {
        name: contact.name,
        email: contact.email,
        phone: contact.phone || undefined,
        recipient: answers.recipient,
        setting: answers.setting,
        interests: answers.interests,
        frequency: answers.frequency,
      });
      setResult({ ...answers, name: contact.name, email: contact.email });
      toast.success("Your match profile has been sent to our care team");
    } catch {
      toast.error(`Something went wrong — please email us at ${EMAIL}`);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers({ recipient: "", setting: "", interests: [], frequency: "" });
    setContact({ name: "", email: "", phone: "" });
    setResult(null);
  };

  if (result) {
    return (
      <div data-testid="quiz-results" className="rounded-3xl border border-sage/30 bg-white p-8 shadow-xl shadow-espresso/10 sm:p-12">
        <div className="mx-auto max-w-2xl text-center">
          <div className="mx-auto mb-5 inline-flex rounded-full bg-sage-soft p-4">
            <Sparkles className="h-9 w-9 text-sage" />
          </div>
          <h3 className="mb-3 font-serif text-2xl font-semibold text-espresso sm:text-3xl">
            {result.name.split(" ")[0]}'s match profile is ready
          </h3>
          <p className="mb-8 text-lg text-warmgray">
            A companion for <strong className="text-espresso">{result.recipient.toLowerCase()}</strong> living{" "}
            <strong className="text-espresso">{result.setting.toLowerCase()}</strong>, visiting{" "}
            <strong className="text-espresso">{result.frequency.toLowerCase()}</strong> — who shares what they love:
          </p>
          <ul className="mb-8 flex flex-wrap justify-center gap-3" data-testid="quiz-recommendations">
            {result.interests.map((i) => (
              <li key={i} className="flex items-center gap-2 rounded-full border border-brand/25 bg-brand-soft/70 px-5 py-2.5 text-base font-semibold text-espresso">
                <Check className="h-4 w-4 text-brand" /> {RECOMMENDATIONS[i] || i}
              </li>
            ))}
          </ul>
          <p className="mb-8 rounded-2xl bg-sage-soft/70 p-5 text-base text-espresso/80 sm:text-lg">
            Our care team has received your profile and will say a friendly hello at{" "}
            <strong>{result.email}</strong> soon — with companion suggestions made just for them.
          </p>
          <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link to="/contact" data-testid="quiz-book-button" className="btn-primary">
              Book a Free Consultation <ArrowRight className="h-5 w-5" />
            </Link>
            <button data-testid="quiz-retake-button" className="btn-outline" onClick={reset}>
              <RotateCcw className="h-5 w-5" /> Start Over
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="companion-quiz" className="rounded-3xl border border-espresso/10 bg-white p-6 shadow-xl shadow-espresso/10 sm:p-10">
      <div className="mb-8">
        <div className="mb-3 flex items-center justify-between text-sm font-semibold text-warmgray">
          <span data-testid="quiz-progress-label">
            {isContactStep ? "Last step — where do we send the match?" : `Question ${step + 1} of ${QUESTIONS.length}`}
          </span>
          <span>{Math.round(((step + 1) / TOTAL_STEPS) * 100)}%</span>
        </div>
        <div className="h-2.5 overflow-hidden rounded-full bg-sand">
          <motion.div
            className="h-full rounded-full bg-brand"
            animate={{ width: `${((step + 1) / TOTAL_STEPS) * 100}%` }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            data-testid="quiz-progress-bar"
          />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        >
          {isContactStep ? (
            <div>
              <h3 className="mb-6 font-serif text-2xl font-semibold text-espresso sm:text-3xl" data-testid="quiz-contact-title">
                Almost there — who's asking?
              </h3>
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="quiz-name" className="input-label">Your name *</label>
                  <input
                    id="quiz-name"
                    data-testid="quiz-name-input"
                    className="input-field"
                    placeholder="e.g. Karen Turner"
                    value={contact.name}
                    onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))}
                  />
                </div>
                <div>
                  <label htmlFor="quiz-email" className="input-label">Email address *</label>
                  <input
                    id="quiz-email"
                    data-testid="quiz-email-input"
                    type="email"
                    className="input-field"
                    placeholder="you@example.com"
                    value={contact.email}
                    onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="quiz-phone" className="input-label">Phone (optional)</label>
                  <input
                    id="quiz-phone"
                    data-testid="quiz-phone-input"
                    type="tel"
                    className="input-field"
                    placeholder="If you'd prefer a call"
                    value={contact.phone}
                    onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div>
              <h3 className="mb-6 font-serif text-2xl font-semibold text-espresso sm:text-3xl" data-testid="quiz-question-title">
                {question.title}
              </h3>
              <div className="flex flex-wrap gap-3">
                {question.options.map((opt, i) => {
                  const selected = question.multi
                    ? answers.interests.includes(opt)
                    : answers[question.key] === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      data-testid={`quiz-option-${question.key}-${i}`}
                      aria-pressed={selected}
                      onClick={() => pick(opt)}
                      className={`rounded-full border-2 px-5 py-3 text-base font-semibold transition-all duration-200 sm:text-lg ${
                        selected
                          ? "border-brand bg-brand text-white shadow-md shadow-brand/25"
                          : "border-espresso/15 bg-cream text-espresso hover:border-brand hover:text-brand"
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="mt-9 flex items-center justify-between gap-4">
        <button
          type="button"
          data-testid="quiz-back-button"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          className="btn-outline !border-espresso/15 disabled:invisible"
        >
          <ArrowLeft className="h-5 w-5" /> Back
        </button>
        {isContactStep ? (
          <button
            type="button"
            data-testid="quiz-submit-button"
            onClick={submit}
            disabled={!answered || submitting}
            className="btn-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" /> Sending…
              </>
            ) : (
              <>
                See My Match <Sparkles className="h-5 w-5" />
              </>
            )}
          </button>
        ) : (
          <button
            type="button"
            data-testid="quiz-continue-button"
            onClick={() => setStep((s) => s + 1)}
            disabled={!answered}
            className="btn-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            Continue <ArrowRight className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
};
